import { Component } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';

/**
 * Admin mode › Flexi › Settings: switches between the HSE advisor page, the
 * safety-round template library and the exposure assessment template library.
 * All are children of the one Settings tab (218), so this is a page-level
 * strip rather than another Flexi tab.
 */
@Component({
  selector: 'flexi-settings-nav',
  template: `
    <nav class="fsn" [attr.aria-label]="'FLEXI_SETTINGS.TAB' | translate">
      <a
        class="fsn__link"
        [routerLink]="settingsUrl"
        routerLinkActive="fsn__link--active"
        [routerLinkActiveOptions]="exactPath"
        ariaCurrentWhenActive="page"
      >{{ 'FLEXI_SAFETY_ROUNDS.SETTINGS_NAV.ADVISORS' | translate }}</a>
      <a
        class="fsn__link"
        [routerLink]="settingsUrl + '/safety-round-templates'"
        routerLinkActive="fsn__link--active"
        ariaCurrentWhenActive="page"
      >{{ 'FLEXI_SAFETY_ROUNDS.SETTINGS_NAV.TEMPLATES' | translate }}</a>
      <a
        class="fsn__link"
        [routerLink]="settingsUrl + '/exposure-templates'"
        routerLinkActive="fsn__link--active"
        ariaCurrentWhenActive="page"
      >{{ 'FLEXI_EXPOSURE.SETTINGS_NAV.TEMPLATES' | translate }}</a>
    </nav>
  `,
  styles: [`
    :host { display: block; margin-bottom: 16px; }
    .fsn {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 3px;
      padding: 3px;
      border: 1px solid var(--flexi-line);
      border-radius: var(--flexi-radius-sm);
      background: var(--flexi-surface);
    }
    .fsn__link {
      display: inline-flex;
      align-items: center;
      min-height: 36px;
      padding: 0 14px;
      border-radius: 7px;
      color: var(--flexi-body);
      font-size: 14px;
      font-weight: 700;
      text-decoration: none;
    }
    .fsn__link:hover { background: var(--flexi-surface-muted); }
    .fsn__link:focus-visible { outline: 2px solid var(--flexi-accent); outline-offset: 2px; }
    .fsn__link--active,
    .fsn__link--active:hover {
      background: var(--flexi-accent);
      color: var(--flexi-on-accent);
    }
  `],
  standalone: false,
})
export class FlexiSettingsNavComponent {
  /**
   * The advisor page is active on its own path only. Matrix parameters are
   * ignored: the shell opens the first tab as `settings;Id=…`, which a plain
   * `exact: true` would treat as a different URL.
   */
  readonly exactPath: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  constructor(private router: Router) {}

  /** `/app/admin/{id}/Flexi/settings`, from whatever prefix the shell is mounted under. */
  get settingsUrl(): string {
    return `${this.router.url.split('/Flexi')[0]}/Flexi/settings`;
  }
}
